// plants/shell.jsx — the Plants page: a full takeover, opened from the
// home-screen line or the menu, plus that line itself. Built from the
// canvas Ben approved on 2026-10-22.
//
// One card per plant, most urgent first: OpenPlantbook photo, status in
// words, today's moisture on a bar with the plant's three lines, last
// watered (from the readings), one plain sentence, 30 days of moisture,
// and OpenPlantbook's care notes. Two columns on the iPad, one on a phone.
//
// Colour: the watering point is rust on every card (Ben: it marks where
// "needs you" begins); the reading and the status turn rust only when the
// plant is below it. Everything else is ink.

import * as React from 'react';
import { TakeoverShell, Card, fs } from '../direction-a.jsx';
import { FONT, RADIUS } from '../theme.js';
import { usePlantHistory } from './use-plants.jsx';
import { history, note, wateredWhen, homeLine } from './model.js';

function useWidth() {
  const ref = React.useRef(null);
  const [w, setW] = React.useState(0);
  React.useLayoutEffect(() => {
    if (!ref.current) return undefined;
    const ro = new ResizeObserver(([e]) => setW(Math.floor(e.contentRect.width)));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

const sans = { fontFamily: FONT.sans };
const label = (T) => ({ fontSize: fs(T, 13), color: T.inkSoft });

function Dot({ color, size = 8 }) {
  return <span aria-hidden style={{ width: size, height: size, borderRadius: '50%', background: color, flexShrink: 0 }}/>;
}

// ── The bar: today's reading against the three lines ───────────────
//
// Wet on the left, dry on the right (family feedback, 2026-10-22: a drying
// plant should move towards "water", not away from it). This runs the
// opposite way to every other bar on the panel, on purpose. The dot
// carries the reading itself.

function MoistureBar({ T, p }) {
  const [ref, width] = useWidth();
  const top = Math.max(75, (p.max ?? 60) + 10, (p.moisture ?? 0) + 5);
  const pad = 14;                                   // room for the dot label at the ends
  const x = (v) => pad + (1 - v / top) * (width - 2 * pad);
  const alert = p.status.alert;
  const ok = width > 0 && p.moisture != null;
  const ty = 30;                                    // the track's centre line
  const marks = [
    p.max != null && { v: p.max, text: `too wet ${p.max}%`, color: T.mute, strong: false, kind: 'max' },
    p.water != null && { v: p.water, text: `water ${p.water}%`, color: T.attention, strong: true, kind: 'water' },
    p.min != null && { v: p.min, text: `very dry ${p.min}%`, color: T.mute, strong: false, kind: 'min' },
  ].filter(Boolean);
  // Water and very dry sit close together; push their labels apart.
  const close = p.min != null && p.water != null && x(p.min) - x(p.water) < 90;
  const ink = alert ? T.attention : T.ink;
  return (
    <div ref={ref} style={{ width: '100%' }}>
      {ok && (
        <svg width={width} height={54} role="img" style={{ display: 'block', overflow: 'visible' }}
          aria-label={`${Math.round(p.moisture)}% soil moisture; too wet above ${p.max}%, water at ${p.water}%, very dry below ${p.min}%`}>
          <rect x={pad} y={ty - 3} width={width - 2 * pad} height={6} rx={3} fill={T.cardSoft}/>
          {p.water != null && p.max != null && <rect x={x(p.max)} y={ty - 3} width={x(p.water) - x(p.max)} height={6} fill={T.hair}/>}
          {marks.map((m) => (
            <line key={m.kind} x1={x(m.v)} x2={x(m.v)} y1={ty - 8} y2={m.strong ? ty + 11 : ty + 9}
              stroke={m.color} strokeWidth={m.strong ? 1.8 : 1.2}/>
          ))}
          <circle cx={x(p.moisture)} cy={ty} r={7} fill={ink} stroke={T.card} strokeWidth={2}/>
          <text x={x(p.moisture)} y={ty - 14} textAnchor="middle" fontSize={fs(T, 13)} fontWeight={600} fill={ink}
            stroke={T.card} strokeWidth={3} paintOrder="stroke" style={sans}>
            {Math.round(p.moisture)}%
          </text>
          {marks.map((m) => {
            const anchor = close && m.kind === 'water' ? 'end' : close && m.kind === 'min' ? 'start' : 'middle';
            const tx = close && m.kind === 'water' ? x(m.v) - 4 : close && m.kind === 'min' ? x(m.v) + 4 : x(m.v);
            return (
              <text key={`t${m.kind}`} x={Math.min(Math.max(tx, 0), width)} y={ty + 22} textAnchor={anchor}
                fontSize={fs(T, 12)} fontWeight={m.strong ? 600 : 400} fill={m.strong ? T.attention : T.inkSoft} style={sans}>
                {m.text}
              </text>
            );
          })}
        </svg>
      )}
    </div>
  );
}

// ── 30 days of moisture ────────────────────────────────────────────
//
// Drawn from the hourly readings, so each watering shows as the spike it
// was, with its dot on top: the dot means "watered here", its height how
// wet it got. (The first version drew daily averages, which flatten the
// spike, and the dots floated above the line with no visible meaning.)

function MoistureChart({ T, p, h, now }) {
  const [ref, width] = useWidth();
  if (!h) return <div ref={ref} style={{ height: 112 }}/>;
  const H = 76, top = 6, n = 30;
  const start = new Date(now); start.setHours(0, 0, 0, 0); start.setDate(start.getDate() - (n - 1));
  const t0 = start.getTime(), t1 = now.getTime();
  const tx = (t) => ((t - t0) / (t1 - t0)) * (width - 6) + 3;
  const rows = h.rows.filter((r) => r.start >= t0 && typeof r.mean === 'number');
  // Fit the scale to this plant's own range (watering point included), in
  // tens, so its spikes and drying read clearly instead of hugging zero.
  const vals = [...rows.map((r) => r.mean), p.moisture, p.water].filter((v) => typeof v === 'number');
  const yMin = Math.max(0, Math.floor((Math.min(...vals) - 4) / 10) * 10);
  const yMax = Math.ceil((Math.max(...vals) + 4) / 10) * 10;
  const y = (v) => top + H - ((v - yMin) / (yMax - yMin)) * H;
  // Break the line across a gap of more than two hours without readings.
  const segs = [];
  let cur = [];
  rows.forEach((r, i) => {
    if (i && r.start - rows[i - 1].start > 2 * 3600000) { segs.push(cur); cur = []; }
    cur.push(`${tx(r.start).toFixed(1)},${y(r.mean).toFixed(1)}`);
  });
  if (cur.length) segs.push(cur);
  // Each watering's dot sits on the line's highest point in the six hours
  // after it; one the hourly readings have not caught up with sits on today.
  const drops = h.waterings.filter((w) => w.at.getTime() >= t0).map((w) => {
    const t = w.at.getTime();
    const near = rows.filter((r) => r.start >= t - 3600000 && r.start <= t + 6 * 3600000);
    if (!near.length) return { x: tx(t1), y: y(p.moisture ?? w.peak), key: t };
    const top1 = near.reduce((a, r) => (r.mean > a.mean ? r : a));
    return { x: tx(top1.start), y: y(top1.mean), key: t };
  });
  const firstT = rows[0]?.start;
  const fitted = firstT && firstT - t0 > 3 * 86400000;
  const mid = new Date(t0 + 15 * 86400000);
  const fmt = (d) => d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
  return (
    <div ref={ref} style={{ width: '100%' }}>
      {width > 0 && (
        <svg width={width} height={112} role="img" style={{ display: 'block', overflow: 'visible' }}
          aria-label={`Soil moisture over ${n} days${drops.length ? `, watered ${drops.length} times` : ''}`}>
          {p.water != null && <line x1={0} x2={width} y1={y(p.water)} y2={y(p.water)} stroke={T.attention} strokeWidth={1.2} strokeDasharray="4 4"/>}
          <line x1={0} x2={width} y1={top + H} y2={top + H} stroke={T.hairHi}/>
          {segs.map((sg, i) => (
            <polyline key={i} points={sg.join(' ')} fill="none" stroke={T.ink} strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round"/>
          ))}
          {drops.map((d) => <circle key={d.key} cx={d.x} cy={d.y} r={3.4} fill={T.ink} stroke={T.card} strokeWidth={1.5}/>)}
          {p.moisture != null && <circle cx={tx(t1)} cy={y(p.moisture)} r={3.5} fill={p.status.alert ? T.attention : T.ink}/>}
          {p.water != null && (
            <text x={0} y={y(p.water) + 13} fontSize={fs(T, 11)} fontWeight={600} fill={T.attention}
              stroke={T.card} strokeWidth={3} paintOrder="stroke" style={sans}>water {p.water}%</text>
          )}
          <g fontSize={fs(T, 12)} fill={T.inkSoft} style={sans}>
            <text x={0} y={104}>{fitted ? `Sensor since ${fmt(new Date(firstT))}` : fmt(start)}</text>
            <text x={width / 2} y={104} textAnchor="middle">{fmt(mid)}</text>
            <text x={width} y={104} textAnchor="end">Today</text>
          </g>
          <g fontSize={fs(T, 11)} fill={T.mute} style={sans}>
            <circle cx={width - 92} cy={9} r={3} fill={T.ink}/>
            <text x={width - 84} y={13}>watered</text>
          </g>
        </svg>
      )}
    </div>
  );
}

// ── One plant ──────────────────────────────────────────────────────

const CARE = [
  ['watering', 'Watering'], ['sunlight', 'Light'], ['fertilization', 'Feeding'], ['soil', 'Soil'], ['pruning', 'Pruning'],
];

function PlantCard({ T, p, h, now, phone }) {
  const s = p.status;
  const alert = s.alert;
  const words = note(p, h, now);
  const last = h?.last ? wateredWhen(h.last.at, now) : h ? 'Not in the last 30 days' : null;
  const count = h ? h.waterings.filter((w) => now - w.at < 30 * 86400000).length : null;
  return (
    <Card T={T} style={{ gap: 16, padding: phone ? '18px 20px 20px' : '22px 24px' }}>
      <div style={{ display: 'flex', gap: phone ? 14 : 16, alignItems: 'center' }}>
        {p.image && (
          <img src={p.image} alt={`${p.name}, OpenPlantbook photo`} loading="lazy"
            style={{ width: phone ? 64 : 88, height: phone ? 64 : 88, objectFit: 'cover', borderRadius: phone ? 10 : 12, flexShrink: 0, background: T.cardSoft }}/>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
          <h2 style={{ margin: 0, fontFamily: FONT.serif, fontWeight: 500, fontSize: phone ? 24 : 28, lineHeight: 1.05, color: T.ink }}>{p.name}</h2>
          <span style={{ fontSize: fs(T, 13), color: T.inkSoft }}>
            {p.room}{p.species && !phone ? <> · <i>{p.species}</i></> : null}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: fs(T, 15), fontWeight: s.kind === 'fine' ? 500 : 600, color: alert ? T.attention : s.kind === 'fine' ? T.inkSoft : T.ink }}>
            {alert && <Dot color={T.attention}/>}{s.label}
          </span>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span style={{ fontFamily: FONT.serif, fontSize: phone ? 44 : 48, lineHeight: .9, color: T.ink, fontVariantNumeric: 'lining-nums' }}>
            {p.moisture != null ? `${Math.round(p.moisture)}%` : '–'}
          </span>
          <span style={{ fontSize: fs(T, 14), color: T.inkSoft }}>soil moisture</span>
        </div>
        <MoistureBar T={T} p={p}/>
      </div>

      {h && (
        <div style={{ display: 'grid', gridTemplateColumns: phone ? 'minmax(0, 1fr)' : 'repeat(2, minmax(0, 1fr))', gap: phone ? 10 : 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <span style={label(T)}>Last watered</span>
            <span style={{ fontSize: fs(T, 16), fontWeight: 500 }}>{last}</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <span style={label(T)}>Last 30 days</span>
            <span style={{ fontSize: fs(T, 16), fontWeight: 500 }}>{count === 0 ? 'Not watered' : `Watered ${count} time${count === 1 ? '' : 's'}`}</span>
          </div>
        </div>
      )}
      {words && <p style={{ margin: 0, fontSize: fs(T, 14), lineHeight: 1.45, color: p.heard === false ? T.attention : T.inkSoft }}>{words}</p>}

      <MoistureChart T={T} p={p} h={h} now={now}/>

      {p.care && (
        <>
          <div style={{ height: 1, background: T.hair }}/>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span style={{ fontSize: fs(T, 12), letterSpacing: '.12em', textTransform: 'uppercase', color: T.inkSoft, fontWeight: 500 }}>Care, from OpenPlantbook</span>
            <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: phone ? 'minmax(0, 1fr)' : '92px minmax(0, 1fr)', columnGap: 12, rowGap: phone ? 2 : 6, fontSize: fs(T, 14), lineHeight: 1.4 }}>
              {CARE.filter(([k]) => p.care[k]).map(([k, name]) => (
                <React.Fragment key={k}>
                  <dt style={{ color: T.inkSoft, fontSize: fs(T, phone ? 13 : 14), marginTop: phone ? 6 : 0 }}>{name}</dt>
                  <dd style={{ margin: 0, color: T.ink }}>{p.care[k]}</dd>
                </React.Fragment>
              ))}
            </dl>
          </div>
        </>
      )}
    </Card>
  );
}

// ── The page ───────────────────────────────────────────────────────

export function PlantsShell({ open, ...props }) {
  if (!open) return null;
  return <PlantsPage {...props}/>;
}

function PlantsPage({ T, onClose, plants, viewport, hass, now }) {
  const rows = usePlantHistory(hass, true);
  const phone = viewport === 'phone';
  const line = homeLine(plants);
  const silent = plants.filter((p) => p.heard === false);
  const soon = plants.filter((p) => p.status.kind === 'soon');
  const others = line ? plants.length - plants.filter((p) => p.status.alert).length : null;

  const cards = plants.map((p) => {
    const h = rows ? history(rows[p.entity] || [], now, p) : null;
    return <PlantCard key={p.id} T={T} p={p} h={h} now={now} phone={phone}/>;
  });

  return (
    <TakeoverShell T={T} viewport={viewport} eyebrow="Wallace St" title="Plants" onClose={onClose} bodyless>
      <div style={{
        padding: phone ? '8px 18px calc(32px + env(safe-area-inset-bottom))' : '12px 36px calc(40px + env(safe-area-inset-bottom))',
        maxWidth: 1180, margin: '0 auto', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: phone ? 16 : 20,
      }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px 18px', flexWrap: 'wrap' }}>
          {line ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: fs(T, 17), fontWeight: 600, color: T.attention }}>
              <Dot color={T.attention} size={9}/>{line.text}
            </span>
          ) : (
            <span style={{ fontSize: fs(T, 17), fontWeight: 600, color: T.ink }}>
              {soon.length ? `Nothing needs water yet. ${soon.map((p) => p.name).join(' and ')} soon.` : 'Nothing needs water.'}
            </span>
          )}
          <span style={{ fontSize: fs(T, 15), color: T.inkSoft }}>
            {line && others ? `The other ${others === 1 ? 'one is' : `${others} are`} fine. ` : ''}
            {silent.length ? `Not heard lately: ${silent.map((p) => p.name).join(', ')}.` : 'All four sensors are reporting.'}
          </span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: phone ? 'minmax(0, 1fr)' : 'repeat(2, minmax(0, 1fr))', gap: phone ? 16 : 20, alignItems: 'start' }}>
          {cards}
        </div>
        <div style={{ fontSize: fs(T, 12), lineHeight: 1.45, color: T.mute, textAlign: 'center' }}>
          Moisture from each plant's soil sensor. Watering points, ranges, photos and care notes from OpenPlantbook, as set in Home Assistant. Waterings are worked out from jumps in the readings.
        </div>
      </div>
    </TakeoverShell>
  );
}

// The home-screen line: only while a plant is below its watering point.
export function PlantsLine({ T, plants, onOpen }) {
  const line = homeLine(plants);
  if (!line) return null;
  return (
    <button onClick={onOpen} aria-label={`Open the plants page: ${line.text}`} style={{
      display: 'flex', alignItems: 'center', gap: 12, width: '100%', boxSizing: 'border-box',
      padding: '14px 20px', border: 'none', borderRadius: RADIUS.control, background: T.card, boxShadow: T.cardShadow,
      fontFamily: FONT.sans, textAlign: 'left', color: T.ink, cursor: 'pointer',
    }}>
      <Dot color={T.attention} size={9}/>
      <span style={{ fontSize: fs(T, 16), fontWeight: 600, color: T.attention }}>{line.text}</span>
      <span style={{ fontSize: fs(T, 15), color: T.inkSoft, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{line.sub}</span>
      <svg style={{ marginLeft: 'auto', flexShrink: 0 }} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.inkSoft} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M9 6l6 6-6 6"/></svg>
    </button>
  );
}
